import React, { useContext, useState } from "react";
import { ValueTypes } from "../../shared/types";
import { IntlShape, useIntl } from "react-intl";
import { messages } from "../messages";
import { getDefaultFields } from "../shared/helper";
import { useProperty } from "../../../../components/property-provider";

const INVENTORY_MANAGEMENT_FILTERS = "INVENTORY_MANAGEMENT_FILTERS";
const INVENTORY_MANAGEMENT_ORDER = "INVENTORY_MANAGEMENT_ORDER";
const SHOW_ONLY_MASTER_LINES = "SHOW_ONLY_MASTER_LINES";

export interface FilterValues {
  value: ValueTypes;
  label: string;
  isChecked?: boolean;
}

interface Filters {
  fields?: FilterValues[];
  roomTypes?: FilterValues[];
  rates?: FilterValues[];
  mealPlans?: FilterValues[];
  occupancies?: FilterValues[];
}

interface Order {
  orderBy: {
    value: string;
    label: string;
    isChecked?: boolean;
  };
}

export interface AllValues {
  fields?: FilterValues[];
  roomTypes?: FilterValues[];
  rates?: FilterValues[];
  mealPlans?: FilterValues[];
  occupancies?: FilterValues[];
  showOnlyMasterLines?: boolean;
  order?: {
    value: string;
    label: string;
    isChecked?: boolean;
  };
  inventoryLineSearched?: string;
}

export const SortAndFilterContext = React.createContext({
  storeFilters: (props: any) => {},
  getFilters: () => ({} as Filters),
  storeOrder: (props: any) => {},
  getOrder: () => ({} as Order | null),
  clearAll: () => {},
  markShowOnlyMasterLines: (isSelected: boolean) => {},
  isShowOnlyMasterLinesSelected: (): boolean => ({} as boolean),
  storeInventoryLineSearched: (prop: any) => {},
  getInventoryLineSearched: (): string | undefined => ({} as string | undefined),
  allValues: {} as AllValues,
  apply: () => {},
});

const getInitialFilters = (intl: IntlShape, propertyTicker: string) => {
  const storedData = window.localStorage.getItem(INVENTORY_MANAGEMENT_FILTERS);
  if (storedData) {
    const parsedData = JSON.parse(storedData);
    if (parsedData[propertyTicker]) {
      return parsedData[propertyTicker];
    }
  }
  return {
    fields: getDefaultFields(intl),
  };
};

const getInitialOrder = (intl: IntlShape) => {
  const storedData = window.localStorage.getItem(INVENTORY_MANAGEMENT_ORDER);
  if (storedData) {
    return JSON.parse(storedData);
  }

  return {
    orderBy: { value: "roomtype", label: intl.formatMessage(messages.roomTypes), isChecked: true },
  };
};

const getInitialShowOnlyMasterLines = (): boolean =>
  window.localStorage.getItem(SHOW_ONLY_MASTER_LINES) === "true";

export const SortAndFilterContextProvider = ({ children }: any) => {
  const intl = useIntl();
  const { ticker: propertyTicker } = useProperty();
  const [filters, setFilters] = useState<Filters>(getInitialFilters(intl, propertyTicker));
  const [order, setOrder] = useState(getInitialOrder(intl));
  const [showOnlyMasterLines, setShowOnlyMasterLines] = useState<boolean>(
    getInitialShowOnlyMasterLines()
  );
  const [inventoryLineSearched, setInventoryLineSearched] = useState<string | undefined>(undefined);
  const [allValues, setAllValues] = useState<AllValues>({
    fields: filters.fields,
    roomTypes: filters.roomTypes,
    rates: filters.rates,
    mealPlans: filters.mealPlans,
    occupancies: filters.occupancies,
    showOnlyMasterLines,
    order,
    inventoryLineSearched,
  });

  const saveOnLocalStorage = (key: string, props: any) => {
    window.localStorage.setItem(key, JSON.stringify(props));
  };

  const getFromLocalStorage = (key: string) => {
    const storedData = window.localStorage.getItem(key);
    if (storedData) {
      return JSON.parse(storedData || "{}");
    }
  };

  const removeOnLocalStorage = (key: string) => window.localStorage.removeItem(key);

  const storeFilters = (props: any) => {
    const data = { ...filters, ...props };
    setFilters(data);
  };

  const getFilters = () => {
    return filters;
  };

  const storeOrder = (props: any) => {
    setOrder(props);
  };

  const getOrder = () => {
    return order;
  };

  const markShowOnlyMasterLines = (isSelected: boolean) => {
    setShowOnlyMasterLines(isSelected);
  };

  const isShowOnlyMasterLinesSelected = (): boolean => {
    return showOnlyMasterLines;
  };

  const storeInventoryLineSearched = (prop: any) => {
    setInventoryLineSearched(prop);
  };

  const getInventoryLineSearched = (): string | undefined => {
    return inventoryLineSearched;
  };

  const clearAll = () => {
    const filters = getFromLocalStorage(INVENTORY_MANAGEMENT_FILTERS);
    // @ts-ignore
    delete filters[propertyTicker];
    saveOnLocalStorage(INVENTORY_MANAGEMENT_FILTERS, filters);
    removeOnLocalStorage(INVENTORY_MANAGEMENT_ORDER);
    removeOnLocalStorage(SHOW_ONLY_MASTER_LINES);

    const defaultFilters = { fields: getDefaultFields(intl) };
    const order = getInitialOrder(intl);
    setFilters(defaultFilters);
    setOrder(order);
    setInventoryLineSearched(undefined);
    setShowOnlyMasterLines(false);
    setAllValues({ ...defaultFilters, showOnlyMasterLines: false, order });
  };

  const apply = () => {
    const finalFilters = {
      ...getFromLocalStorage(INVENTORY_MANAGEMENT_FILTERS),
      [propertyTicker]: filters,
    };
    saveOnLocalStorage(INVENTORY_MANAGEMENT_FILTERS, finalFilters);
    saveOnLocalStorage(INVENTORY_MANAGEMENT_ORDER, order);
    saveOnLocalStorage(SHOW_ONLY_MASTER_LINES, showOnlyMasterLines);
    setAllValues({
      fields: filters.fields,
      roomTypes: filters.roomTypes,
      rates: filters.rates,
      mealPlans: filters.mealPlans,
      occupancies: filters.occupancies,
      showOnlyMasterLines,
      order: order.orderBy,
      inventoryLineSearched,
    });
  };

  return (
    <SortAndFilterContext.Provider
      value={{
        storeFilters,
        getFilters,
        storeOrder,
        getOrder,
        clearAll,
        markShowOnlyMasterLines,
        isShowOnlyMasterLinesSelected,
        storeInventoryLineSearched,
        getInventoryLineSearched,
        allValues,
        apply,
      }}>
      {children}
    </SortAndFilterContext.Provider>
  );
};

export function useSortAndFilter() {
  const context = useContext(SortAndFilterContext);
  if (context == null) {
    throw new Error(
      "You must call useSortAndFilterContext only when inside a SortAndFilterContextProvider."
    );
  }
  return context;
}
